import { useFormik } from 'formik';
import { ChangeEvent, FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { MultiSelect } from 'react-multi-select-component';
import { useHistory } from 'react-router-dom';
import { RolesAPI } from '../../../api/roleApi';
import Button from '../../../components/Button';
import Input from '../../../components/Input';
import Section from '../../../components/Section';
import TextArea from '../../../components/TextArea';
import { useLanguage } from '../../../contexts/language/language-context';
import { useGetEducationMaterialGroupsQuery } from '../../../services/education-material-groups';
import { FamilyUserRole } from '../../../store/family-user-role/types';
import { DroppedFile, FileCategory } from '../../../types/dropped-file';
import { EducationMaterial } from '../../../types/education-materials/education-material';
import { PartialBy } from '../../../types/partial-by';
import Tags from '../../property/Tags';
import { LoadedFilePesenter } from './loaded-file-pesenter';
import MaterialFileDrop from './material-file-drop';
import { MaterialsList } from './materials-list';

interface EducationMaterialFormProps {
  initialValues: { [key: string]: PartialBy<EducationMaterial, '_id' | 'file' | 'mimeType'> };
  isEdit?: boolean;
  onSubmit: (
    values: { [key: string]: PartialBy<EducationMaterial, '_id' | 'file' | 'mimeType'> },
    droppedFile: DroppedFile | null,
    customThumbnail: DroppedFile | null,
  ) => void;
}

export const EducationMaterialForm: FC<EducationMaterialFormProps> = memo(({ initialValues, isEdit, onSubmit }) => {
  const [droppedFile, setDroppedFile] = useState<DroppedFile | null>(null);
  const [_customThumbnail, set_CustomThumbnail] = useState<DroppedFile | null>(null);
  const [familyUserRoles, setFamilyUserRoles] = useState<FamilyUserRole[]>([]);
  const [selectedVisibleFor, setSelectedVisibleFor] = useState<Array<{ label: string, value: string }>>([]);

  useEffect(() => {
    RolesAPI.queryFamilyUserRoles()
      .then(roles => {
        setFamilyUserRoles(roles);
      })
      .catch(e => {
        console.error('Failed to fetch user roles:', e);
      });
  }, []);

  const selectOptions = useMemo(() => {
    return familyUserRoles.map((role) => ({ label: role.name, value: role._id || '' }));
  }, [familyUserRoles]);

  const accessForArray = useMemo(() => {
    const accessFor = initialValues?.en?.accessFor;
    if (!accessFor?.value || !Array.isArray(accessFor.value)) return [];
    
    return accessFor.value.map((role) => role._id);
  }, [initialValues?.en?.accessFor]);

  useEffect(() => {
    if (familyUserRoles.length > 0 && accessForArray.length > 0) {
      const initialSelected = accessForArray.reduce((accum, current) => {
        const role = familyUserRoles.find(r => r._id === current);
        if (role && role._id) {
          return [...accum, { label: role.name, value: role._id }];
        }
        return accum;
      }, [] as Array<{ label: string, value: string }>);
      setSelectedVisibleFor(initialSelected);
    } else if (accessForArray.length === 0 && familyUserRoles.length > 0) {
      setSelectedVisibleFor([]);
    }
  }, [familyUserRoles, accessForArray]);

  const { 
    data: educationMaterialGroups,
    isFetching: isEducationMaterialGroupsFetching
  } = useGetEducationMaterialGroupsQuery();

  const suggestedMaterialOptions = useMemo(() => {
    if (!educationMaterialGroups || isEducationMaterialGroupsFetching) {
      return [];
    }

    const out: Array<{ label: string, value: string }> = [];

    educationMaterialGroups.forEach(group => {
      const groupName = group.title;

      group.materials.forEach(material => {
        if (isEdit && material._id === initialValues?.en?._id) {
          return;
        }
        out.push({ label: `${groupName} > ${material.name}`, value: material._id });
      })
    });

    return out;
  }, [educationMaterialGroups, isEducationMaterialGroupsFetching, isEdit, initialValues?.en?._id]);

  const [selectedSuggestedMaterials, setSelectedSuggestedMaterials] 
  = useState<Array<{ label: string, value: string }>>([]);

  useEffect(() => {
    if (isEdit && initialValues?.en?.suggestedMaterials && Array.isArray(initialValues.en.suggestedMaterials)) {
      setSelectedSuggestedMaterials(
        suggestedMaterialOptions.reduce((accum, current) => {
          if (~initialValues.en.suggestedMaterials.findIndex((material) => (material as EducationMaterial)._id as string === current.value)) {
            return [...accum, current];
          }
          return accum;
        }, [] as {
          label: string;
          value: string;
          }[])
      );
    } else {
      setSelectedSuggestedMaterials([]);
    }
  }, [initialValues?.en?.suggestedMaterials, initialValues?.suggestedMaterials, isEdit, suggestedMaterialOptions]);

  const selectedSuggestedMaterialsPreview = useMemo(() => {
    const out: Array<EducationMaterial> = [];

    if (!educationMaterialGroups || (educationMaterialGroups && !educationMaterialGroups.length)) {
      return out;
    }

    const educationMaterials = educationMaterialGroups.reduce((acc, current) => {
      return acc.concat(current.materials);
    }, [] as Array<EducationMaterial>);

    const map: { [id: string]: EducationMaterial } = {};

    let findArrayCounter = 0;

    for (let i = 0; i < selectedSuggestedMaterials.length; i++) {
      const currentId = selectedSuggestedMaterials[i].value;

      if (Object.prototype.hasOwnProperty.call(map, currentId)) {
        out.push(map[currentId]);
      }

      if (findArrayCounter >= educationMaterials.length) {
        continue;
      }

      for (;findArrayCounter < educationMaterials.length; findArrayCounter++) {
        const currentMaterial = educationMaterials[findArrayCounter];

        if (currentMaterial._id === currentId) {
          out.push(currentMaterial);
          continue;
        }

        map[currentMaterial._id] = currentMaterial;
      }
    }

    return out;
  }, [selectedSuggestedMaterials, educationMaterialGroups]);

  const history = useHistory();

  const translationsFormiks = {
    en: useFormik({
      initialValues: initialValues?.en || {},
      enableReinitialize: isEdit,
      onSubmit: () => {},
    }),
  }

  const { currentLanguage } = useLanguage();

  const {
    handleChange: translatableHandleChange,
    setFieldValue: translatableSetFieldValue,
    values: translatableValues,
    // errors: translatableErrors,
    resetForm: resetTranslatableForm,
  } = translationsFormiks[currentLanguage];

  const {
    // handleChange: baseHandleChange,
    setFieldValue: baseSetFieldValue,
    values: baseValues,
    // errors: baseErrors,
    resetForm: resetBaseForm,
  } = translationsFormiks['en'];
  const { file, mimeType, customThumbnail, tags = [] } = baseValues;

  const _onSubmit = useCallback(() => {
    onSubmit({ 
        en: {
          ...baseValues,
          accessFor: { value: selectedVisibleFor.map(({ value }) => value) as any },
          suggestedMaterials: selectedSuggestedMaterials.map(({ value }) => value),
        },
      },
      droppedFile,
      _customThumbnail,
    );
      resetTranslatableForm();
      resetBaseForm();
      setSelectedVisibleFor([]);
      setSelectedSuggestedMaterials([])
      setDroppedFile(null);
      history.goBack();
  }, [
    _customThumbnail, 
    baseValues,
    droppedFile,
    history,
    onSubmit,
    resetBaseForm,
    resetTranslatableForm,
    selectedSuggestedMaterials,
    selectedVisibleFor,
  ]);

  const onDescriptionChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    translatableSetFieldValue('description', event.target.value);
  },
  [translatableSetFieldValue]);
console.log(selectedVisibleFor, "selectedVisibleFor")
  return (
    <>
      <div className='card'>
        <div className='card-header'>
          <div className='d-flex flex-1 justify-content-between align-items-center'>
            <h2 className='card-header-title'>{initialValues?._id ? 'Edit material' : 'Add material'}</h2>
          </div>
        </div>
        <Section label='Description'>
          <>
            <Input containerClass='mb-2' label='Name' name='name' value={translatableValues.name} onChange={translatableHandleChange} />
            <TextArea
              label='Description'
              className='w-100 mb-2'
              onChange={onDescriptionChange}
              value={translatableValues.description}
            />
            <div className='d-flex flex-wrap-small' style={{ zIndex: 1001 }}>
              <label className='label-fix-width'>Visible for</label>
              <MultiSelect
                disableSearch
                className='w-100'
                options={selectOptions}
                value={selectedVisibleFor}
                onChange={setSelectedVisibleFor}
                labelledBy='Select'
              />
            </div>
            <Tags 
              tags={tags || []}
              callback={(updated) => {
                baseSetFieldValue('tags', updated);
              }}
            />
          </>
        </Section>

        <Section label='Related Materials'>
          <>
            <div className='d-flex flex-wrap-small'>
              <label className='label-fix-width'>Materials</label>
              <MultiSelect
                disableSearch
                className='w-100'
                options={suggestedMaterialOptions}
                value={selectedSuggestedMaterials}
                onChange={setSelectedSuggestedMaterials}
                labelledBy='Select'
              />
            </div>
            <div className='d-flex flex-wrap-small'>
              <label className='label-fix-width'>Preview</label>
              <MaterialsList materials={selectedSuggestedMaterialsPreview} readonly />
            </div>
          </>
        </Section>

        <Section label='File'>
          <>
            {!!file && !!mimeType && 
              <LoadedFilePesenter fileUrl={file} mimeType={mimeType} />
            }
            <MaterialFileDrop
              onDrop={setDroppedFile}
              droppedFile={droppedFile}
              category={FileCategory.EDUCATION_MATERIAL}
            />
          </>
        </Section>

        <Section label='Thumbnail'>
          <>
            {customThumbnail ? (
                !!mimeType && <LoadedFilePesenter fileUrl={customThumbnail} mimeType={'image/'} />
              ) 
                : 
              (
                <div
                  className={`text-center mb-4 p-1`}
                >
                  <span>No Thumbnail Selected</span>
                </div>
              )
            }
            <MaterialFileDrop
              onDrop={set_CustomThumbnail}
              droppedFile={_customThumbnail}
              category={FileCategory.EDUCATION_MATERIAL_THUMBNAIL}
              description="JPEG or PNG with suggested dimensions 300.x 120"
              accept={['image/jpeg', 'image/png']}
            />
          </>
        </Section>
      </div>
      <div className='bottom-fixed-save bg-light p-3 align-items-center flex-wrap'>
        <div className='pt-2'>
          <Button
            className='btn btn-success'
            label='Save'
            onClick={_onSubmit}
          />
        </div>
      </div>
    </>
  );
});
